"use client";

import { useLayoutEffect } from "react";
import type { RefObject } from "react";

/** Trois copies de la série : la piste montre toujours la copie du milieu. */
export const LOOP_COPIES = 3;

/**
 * La piste qui boucle sans fin, poussée au geste seulement : doigt, pavé
 * tactile, glisser à la souris, flèches. Aucune animation, le défilement est
 * natif et la boucle recale la position quand une copie entière a été
 * parcourue, donc il n'y a rien à faire pour `prefers-reduced-motion`.
 *
 * Extrait de `CardMarquee` le 24/09/2026, à l'identique, pour que
 * `SiteShowcase` en variante `marquee` s'en serve aussi. Une seule mécanique de
 * boucle dans la maison : si elle se corrige un jour, elle se corrige partout.
 *
 * Le DOM attendu : une piste qui contient trois listes portant `data-set="0"`,
 * `"1"`, `"2"`, et, dans son parent, deux boutons `data-marquee-prev` et
 * `data-marquee-next`.
 */
export function useLoopTrack(trackRef: RefObject<HTMLDivElement | null>, itemCount: number): void {
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const setWidth = (): number => {
      const first = track.querySelector<HTMLElement>('[data-set="0"]');
      const second = track.querySelector<HTMLElement>('[data-set="1"]');
      if (!first || !second) return 0;
      return second.offsetLeft - first.offsetLeft;
    };
    // Départ sur la copie du milieu : la première carte est alignée sur le
    // titre, la dernière déborde à gauche, et la piste est sans fin dans les
    // deux sens dès le premier geste. La largeur d'une copie se relit à chaque
    // usage : au montage, la feuille de style peut ne pas être appliquée
    // (copies empilées, largeur nulle) et une valeur figée rendrait boucle et
    // flèches muettes ; le recentrage se fait dès qu'elle est connue.
    let centered = false;
    const center = (): void => {
      if (centered) return;
      const width = setWidth();
      if (width <= 0) return;
      track.scrollLeft = width;
      centered = true;
    };
    // Fenêtre de positions admises sur les trois copies : [0,35 ; 1,65] largeur.
    // Plus large que [0,5 ; 1,5] pour qu'un pas de flèche pré-recalé d'une copie
    // retombe dedans (sinon le recalage au défilement annulerait le pas), plus
    // étroite que [0 ; 2] pour qu'un écran de 1,35 copie ne voie jamais le bord.
    const LOW = 0.35;
    const HIGH = 1.65;
    const wrap = (): void => {
      const width = setWidth();
      if (width <= 0) return;
      if (track.scrollLeft > width * HIGH) track.scrollLeft -= width;
      else if (track.scrollLeft < width * LOW) track.scrollLeft += width;
    };
    center();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => { center(); wrap(); });
    observer?.observe(track);
    const onResize = (): void => wrap();
    track.addEventListener("scroll", wrap, { passive: true });
    const view = track.ownerDocument.defaultView;
    view?.addEventListener("resize", onResize, { passive: true });

    // Glisser à la souris (le doigt et le pavé tactile passent par le défilement natif).
    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    let moved = 0;
    const down = (event: PointerEvent): void => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      dragging = true;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      moved = 0;
      track.setPointerCapture(event.pointerId);
      track.dataset.dragging = "true";
    };
    const move = (event: PointerEvent): void => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      moved = Math.max(moved, Math.abs(delta));
      track.scrollLeft = startScroll - delta;
    };
    const up = (event: PointerEvent): void => {
      if (!dragging) return;
      dragging = false;
      delete track.dataset.dragging;
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    };
    /* Une carte peut être un lien (SiteShowcase). Sans ce garde-fou, tirer la
       piste depuis une carte relâche le bouton sur ce lien et navigue : on
       perdrait la position, et le geste deviendrait un piège. Seuil de 6 px
       pour ne pas confondre un clic un peu tremblant avec un glissement. */
    const guardClick = (event: MouseEvent): void => {
      if (moved > 6) {
        event.preventDefault();
        event.stopPropagation();
        moved = 0;
      }
    };
    // Flèches : un pas d'une carte, en passant par la boucle si besoin.
    const reduced = view?.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const step = (direction: 1 | -1): void => {
      const card = track.querySelector<HTMLElement>('[data-set="0"] > li');
      const width = setWidth();
      if (!card || width <= 0) return;
      const gap = Number.parseFloat(view?.getComputedStyle(track).columnGap ?? "0") || 0;
      let target = track.scrollLeft + direction * (card.offsetWidth + gap);
      if (target > width * HIGH) {
        track.scrollLeft -= width;
        target -= width;
      } else if (target < width * LOW) {
        track.scrollLeft += width;
        target += width;
      }
      track.scrollTo({ left: target, behavior: reduced ? "auto" : "smooth" });
    };
    const prev = track.parentElement?.querySelector<HTMLButtonElement>('[data-marquee-prev]');
    const next = track.parentElement?.querySelector<HTMLButtonElement>('[data-marquee-next]');
    const onPrev = (): void => step(-1);
    const onNext = (): void => step(1);
    prev?.addEventListener("click", onPrev);
    next?.addEventListener("click", onNext);
    track.addEventListener("pointerdown", down);
    track.addEventListener("pointermove", move);
    track.addEventListener("pointerup", up);
    track.addEventListener("pointercancel", up);
    track.addEventListener("click", guardClick, true);
    return () => {
      observer?.disconnect();
      prev?.removeEventListener("click", onPrev);
      next?.removeEventListener("click", onNext);
      track.removeEventListener("scroll", wrap);
      view?.removeEventListener("resize", onResize);
      track.removeEventListener("pointerdown", down);
      track.removeEventListener("pointermove", move);
      track.removeEventListener("pointerup", up);
      track.removeEventListener("pointercancel", up);
      track.removeEventListener("click", guardClick, true);
    };
  }, [trackRef, itemCount]);
}
